import * as S from "../style";
import * as React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import MarkdownContent from "../../MarkdownContent";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { COPILOT_MODEL_MAPPING } from "pages/[Patient]/AllRuns";


interface StyledTooltipProps {
  title: string;
  children: React.ReactNode;
  onClick?: () => void;
}
export const StyledTooltip = (props: StyledTooltipProps) => {
  return (
    <Tooltip title={props.title} placement="bottom" PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: { offset: [0, -10] },
        },
      ],
    }}>
      <S.ActionIconButton size="small" onClick={props.onClick}>
        {props.children}
      </S.ActionIconButton>
    </Tooltip>

  )
}
interface SystemPatientResultsProps {
  meta: any;
  text: string;
  runId?: string;
  createdAt?: string;
  tftModel?: string | null;
  copilotModel?: string | null;
}

export function SystemPatientResults(props: SystemPatientResultsProps) {
  const { language, t } = useLanguage();
  const [copied, setCopied] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const timerRef = React.useRef<number | null>(null);

  const handleCopy = async () => {
    const text = (contentRef.current?.innerText || "").trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        setCopied(false);
        timerRef.current = null;
      }, 5000);
    } catch (e) {
      console.error("Copy failed:", e);
    }
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const formattedDate = props.createdAt
    ? new Intl.DateTimeFormat(
        { en: "en-US", fr: "fr-FR", no: "nb-NO" }[language] || "en-US",
        {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      ).format(new Date(props.createdAt))
    : null;

  return (
    <S.PatientBox
      id="system-patient-results-container"
    >
      {(formattedDate || props.runId) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {formattedDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box component="span" sx={{ fontSize: 11, color: "text.disabled", fontWeight: 500, letterSpacing: 0.5 }}>
                  <Typography variant="caption">{t('copilot.ranAt')}: {formattedDate}</Typography>
                </Box>
              </Box>
            )}
            {props.runId && (
              <Box sx={{ display: "flex", alignItems: "center", color: "text.disabled", gap: 0.5 }}>
                <Typography variant="caption">{t('copilot.runId')}:</Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    color: "text.secondary",
                    fontFamily: "monospace",
                    bgcolor: "action.hover",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                  }}
                  title={props.runId}
                >
                  {props.runId}
                </Box>
              </Box>
            )}
          </Box>
          <Box>
            {(props.tftModel || props.copilotModel) && (
              <Tooltip
                placement="bottom-end"
                title={
                  <Box sx={{ fontSize: 11, lineHeight: 1.6 }}>
                    {props.tftModel && (
                      <Box><Box component="span" sx={{ opacity: 0.65 }}>TFT: </Box>{props.tftModel}</Box>
                    )}
                    {props.copilotModel && (
                      <Box><Box component="span" sx={{ opacity: 0.65 }}>Copilot: </Box>{COPILOT_MODEL_MAPPING[props.copilotModel]}</Box>
                    )}
                  </Box>
                }
              >
                <S.ActionIconButton size="small">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                </S.ActionIconButton>
              </Tooltip>
            )}
            <StyledTooltip title={copied ? "Copied" : "Copy"} onClick={handleCopy}>
              {copied ? (
                <CheckRoundedIcon sx={{ fontSize: 16, color: "success.main" }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              )}
            </StyledTooltip>
          </Box>
        </Box>
      )}
      <Box sx={{ position: "relative" }}>
        <div ref={contentRef}>
          <MarkdownContent content={props.text} />
        </div>
      </Box>
    </S.PatientBox>
  )
}
